import React, { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader, Card } from "@/components/Bits";
import { Upload, FileText, CheckCircle2, AlertTriangle, Building2, Wrench } from "lucide-react";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";

const REQUIRED = ["symbol", "side", "quantity", "entry_price", "entry_time"];
const OPTIONAL = ["exit_price", "exit_time", "fees", "notes", "setup"];

const SAMPLE_CSV = `symbol,side,quantity,entry_price,exit_price,entry_time,exit_time,fees,setup
AAPL,long,100,150.50,152.10,2026-01-15T09:35,2026-01-15T10:10,1.50,breakout
TSLA,short,50,245.20,242.10,2026-01-15T11:00,2026-01-15T13:30,1.00,reversal
NVDA,long,30,520.10,528.50,2026-01-16T09:45,2026-01-16T11:20,1.20,trend
SPY,short,200,485.30,483.10,2026-01-16T13:00,2026-01-16T14:45,2.00,fade
META,long,40,395.20,388.50,2026-01-17T09:40,2026-01-17T10:30,1.40,breakout`;

export default function ImportPage() {
  const [params] = useSearchParams();
  const [file, setFile] = useState(null);
  // Preselect broker from `?broker=webull` query param when arriving via Connections page.
  const [broker, setBroker] = useState(params.get("broker") || "");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [lastRepairResult, setLastRepairResult] = useState(null);
  const [matchDate, setMatchDate] = useState("");
  const [matchTotal, setMatchTotal] = useState("");
  const [matching, setMatching] = useState(false);
  const inputRef = useRef(null);
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data: brokers = [] } = useQuery({
    queryKey: ["brokers"],
    queryFn: async () => (await api.get("/brokers")).data,
  });

  const onUpload = async () => {
    if (!file) return;
    setLoading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (broker) fd.append("broker", broker);
      // Auto-retry on transient 5xx / Cloudflare errors — the /trades/import
      // request runs synchronously and can take 10-60s for large CSVs, which
      // makes it prone to Cloudflare 520/524 timeouts. Idempotency: the
      // backend dedupes by broker_id + fill hash, so a retry after a
      // partially-succeeded import will NOT double-insert trades.
      let r;
      let attempts = 0;
      while (true) {
        try {
          r = await api.post("/trades/import", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 3 * 60 * 1000,  // 3 min — larger CSVs take a while
          });
          break;
        } catch (err) {
          const status = err?.response?.status;
          // Don't retry 4xx (auth/bad-file) — those won't heal on retry.
          if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
            throw err;
          }
          attempts++;
          if (attempts >= 3) throw err;
          await new Promise((res) => setTimeout(res, 2000 * attempts));  // 2s, 4s, 6s
        }
      }
      setResult(r.data);

      // Auto-roll fills → position-cycle trades. Skip when the import produced clean
      // round trips already (mode === "clean_round_trips") — saves a DB scan and
      // avoids touching pre-existing data unnecessarily. Manual button stays as a fallback.
      let rolledMsg = "";
      if (r.data?.mode === "clean_round_trips") {
        rolledMsg = ` · ${r.data.round_trips} clean round trip${r.data.round_trips === 1 ? "" : "s"} — auto-roll skipped`;
      } else {
        try {
          const rep = await api.post("/trades/repair-fills");
          if (rep.data?.round_trips > 0) {
            rolledMsg = ` · auto-rolled ${rep.data.fills_detected} fills → ${rep.data.round_trips} trades`;
          }
        } catch (err) {
          // Silent — user can still click the manual button below.
          console.warn("auto-repair failed; manual button remains available", err);
        }
      }

      qc.invalidateQueries({ queryKey: ["trades"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      qc.invalidateQueries({ queryKey: ["calendar"] });
      const skippedMsg = r.data?.skipped_non_filled > 0 ? ` · ${r.data.skipped_non_filled} non-filled skipped` : "";
      toast.success(`IMPORTED ${r.data.inserted} TRADES${skippedMsg}${rolledMsg}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setLoading(false); }
  };

  const useSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv" });
    setFile(new File([blob], "sample.csv", { type: "text/csv" }));
  };

  const onRepair = async () => {
    if (!window.confirm("Roll fills into position-cycle trades?\n\nThis scans your account for fill-level rows (zero-PnL singletons and previously fill-paired pair-trades), reconstructs the underlying fills, and rolls each open→close→flat cycle into ONE trade with weighted-avg entry/exit prices. Existing position-cycle trades are untouched.")) return;
    setRepairing(true);
    try {
      const r = await api.post("/trades/repair-fills");
      qc.invalidateQueries({ queryKey: ["trades"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      qc.invalidateQueries({ queryKey: ["calendar"] });
      const noop = r.data.fills_detected === 0;
      setLastRepairResult({
        noop,
        fills: r.data.fills_detected || 0,
        roundTrips: r.data.round_trips || 0,
        removed: r.data.removed || 0,
        at: new Date().toISOString(),
      });
      if (noop) {
        toast.success("AUTO-ROLL · NOTHING TO FIX — your trades are already in clean position-cycle form.");
      } else {
        toast.success(`ROLLED · ${r.data.fills_detected} fills → ${r.data.round_trips} trades · removed ${r.data.removed}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Repair failed");
    } finally { setRepairing(false); }
  };

  const onRepairSides = async () => {
    if (!window.confirm("Auto-detect long/short on every trade from entry vs. exit price?\n\nIf entry_price < exit_price → long. If entry_price > exit_price → short. P&L is recomputed for any trades that flip.")) return;
    setRepairing(true);
    try {
      const r = await api.post("/trades/repair-sides");
      qc.invalidateQueries({ queryKey: ["trades"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      qc.invalidateQueries({ queryKey: ["calendar"] });
      qc.invalidateQueries({ queryKey: ["goals"] });
      if (r.data.fixed === 0) {
        toast.success(`SIDES CHECKED · ${r.data.scanned} trades · nothing to flip — directions are already correct.`);
      } else {
        toast.success(`SIDES REPAIRED · flipped ${r.data.fixed} of ${r.data.scanned} trades. P&L recomputed.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Repair sides failed");
    } finally { setRepairing(false); }
  };

  const onMatchBroker = async () => {
    if (!matchTotal) return toast.error("Enter your broker's total P&L first.");
    const target = parseFloat(matchTotal);
    if (isNaN(target)) return toast.error("Broker total must be a number.");
    setMatching(true);
    try {
      const body = { broker_total_pnl: target };
      if (matchDate) body.date = matchDate;
      const r = await api.post("/trades/match-broker-total", body);
      qc.invalidateQueries({ queryKey: ["trades"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      qc.invalidateQueries({ queryKey: ["calendar"] });
      qc.invalidateQueries({ queryKey: ["goals"] });
      if (r.data.adjusted === 0) {
        toast.success(r.data.message || "Already matching your broker.");
      } else {
        toast.success(`MATCHED · $${r.data.delta_applied} prorated as fees across ${r.data.adjusted} trades. App now shows $${target.toFixed(2)}.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Match broker failed");
    } finally { setMatching(false); }
  };

  return (
    <div>
      <PageHeader eyebrow="DATA INGRESS" title="Import Trades from CSV" />

      <div className="p-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">

          <Card eyebrow="STEP 01" title="Pick your broker">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <button onClick={() => setBroker("")} data-testid="broker-auto"
                className={`p-3 text-xs border transition-colors text-left ${broker === "" ? "border-[color:var(--accent)] bg-[rgba(168,85,247,0.12)] text-[color:var(--accent)]" : "border-[color:var(--border)] text-[color:var(--fg-2)] hover:border-[color:var(--accent)]/40"}`}>
                <div className="label-eyebrow text-current">AUTO</div>
                <div className="mt-1">Auto-detect</div>
              </button>
              {brokers.filter(b => b.id !== "generic").map((b) => (
                <button key={b.id} onClick={() => setBroker(b.id)} data-testid={`broker-${b.id}`}
                  className={`p-3 text-xs border transition-colors text-left ${broker === b.id ? "border-[color:var(--accent)] bg-[rgba(168,85,247,0.12)] text-[color:var(--accent)]" : "border-[color:var(--border)] text-[color:var(--fg-2)] hover:border-[color:var(--accent)]/40"}`}>
                  <div className="label-eyebrow text-current"><Building2 className="h-3 w-3 inline mr-1" strokeWidth={1.5}/></div>
                  <div className="mt-1">{b.name}</div>
                </button>
              ))}
            </div>
          </Card>

          <Card eyebrow="STEP 02" title="Upload your CSV">
            <input ref={inputRef} type="file" accept=".csv"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
              className="hidden" data-testid="csv-file-input" />
            <div onClick={() => inputRef.current?.click()}
              className="border border-dashed border-[color:var(--border)] p-10 text-center cursor-pointer hover:border-[color:var(--accent)] transition-colors bg-[color:var(--bg)]"
              data-testid="csv-dropzone">
              <Upload className="h-8 w-8 mx-auto text-[color:var(--fg-3)]" strokeWidth={1.5} />
              <div className="mt-3 text-sm text-[color:var(--fg-2)]">{file ? file.name : "Click to select a CSV file"}</div>
              <div className="label-eyebrow mt-1">{file ? `${(file.size / 1024).toFixed(1)} KB` : "DROP OR CLICK"}</div>
            </div>

            <div className="mt-4 flex items-center gap-3">
              <button onClick={onUpload} disabled={!file || loading} data-testid="csv-upload-button"
                className="bg-white text-black px-4 py-2 text-sm hover:bg-[color:var(--accent)] transition-colors disabled:opacity-40">
                {loading ? "IMPORTING…" : "IMPORT FILE"}
              </button>
              <button onClick={useSample} data-testid="use-sample-csv" className="text-xs text-[color:var(--fg-2)] hover:text-[color:var(--accent)] transition-colors">
                Use sample CSV →
              </button>
            </div>
          </Card>

          <Card eyebrow="REPAIR · MANUAL FALLBACK" title="Trade count looks too high?" className="border-amber-500/30">
            <div className="text-xs text-[color:var(--fg-2)] leading-relaxed">
              CSV imports now <strong className="text-[color:var(--accent)]">auto-roll fill-by-fill rows into position-cycle trades</strong> — usually you won&apos;t need this button. Use it if auto-roll didn&apos;t catch something, or to clean up older trades imported before this feature shipped.
            </div>
            <div className="text-xs text-[color:var(--fg-2)] leading-relaxed mt-2">
              Tradervue / Moredge count <strong className="text-[color:var(--fg)]">one trade per position cycle</strong> (open → close → flat = 1 trade). This rolls every chain of partial fills into a single trade with weighted-avg entry/exit prices and summed P&L. Existing cycle trades are left alone.
            </div>
            <button onClick={onRepair} disabled={repairing} data-testid="repair-fills-btn"
              className="mt-4 inline-flex items-center gap-2 bg-amber-400 text-black px-4 py-2 text-sm hover:bg-amber-300 transition-colors disabled:opacity-40">
              <Wrench className="h-4 w-4" strokeWidth={1.5} />
              {repairing ? "REPAIRING…" : "ROLL FILLS INTO TRADES"}
            </button>
            <button onClick={onRepairSides} disabled={repairing} data-testid="repair-sides-btn"
              className="mt-4 ml-3 inline-flex items-center gap-2 bg-sky-400 text-black px-4 py-2 text-sm hover:bg-sky-300 transition-colors disabled:opacity-40">
              <Wrench className="h-4 w-4" strokeWidth={1.5} />
              {repairing ? "WORKING…" : "AUTO-DETECT LONG/SHORT"}
            </button>
            {lastRepairResult && (
              <div
                className={`mt-3 text-[11px] font-mono-num ${lastRepairResult.noop ? "text-[color:var(--accent)]" : "text-amber-300"}`}
                data-testid="repair-status"
              >
                {lastRepairResult.noop
                  ? "✓ AUTO-ROLL DETECTED NOTHING TO FIX · trades already clean"
                  : `LAST RUN · ${lastRepairResult.fills} fills → ${lastRepairResult.roundTrips} trades · ${lastRepairResult.removed} removed`}
                <span className="text-[color:var(--fg-3)] ml-2">{new Date(lastRepairResult.at).toLocaleTimeString()}</span>
              </div>
            )}
          </Card>

          <Card eyebrow="STEP 03 · MATCH YOUR BROKER" title="Numbers don&apos;t match Webull / your broker?" className="border-sky-500/30">
            <div className="text-xs text-[color:var(--fg-2)] leading-relaxed">
              Webull&apos;s <strong className="text-[color:var(--fg)]">Orders CSV doesn&apos;t include fees</strong> (SEC, FINRA TAF, ECN routing). Your broker dashboard nets those out, so its total P&amp;L is a few pennies lower than ours.
              Paste your broker&apos;s exact total for the same day (or all-time) and we&apos;ll <strong className="text-sky-300">prorate the difference as fees</strong> across the matching trades — after that, your Dashboard total matches your broker to the penny.
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="label-eyebrow text-[color:var(--fg-3)]">DATE (OPTIONAL)</label>
                <input
                  type="date"
                  value={matchDate}
                  onChange={(e) => setMatchDate(e.target.value)}
                  data-testid="match-broker-date"
                  className="mt-1 w-full bg-[color:var(--bg)] border border-[color:var(--border)] px-3 py-2 text-sm text-[color:var(--fg)] focus:border-sky-500/60 focus:outline-none"
                />
                <div className="text-[10px] text-[color:var(--fg-3)] mt-1">Leave blank to adjust all-time totals.</div>
              </div>
              <div>
                <label className="label-eyebrow text-[color:var(--fg-3)]">BROKER TOTAL P&L ($)</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="e.g. 3.32"
                  value={matchTotal}
                  onChange={(e) => setMatchTotal(e.target.value)}
                  data-testid="match-broker-total"
                  className="mt-1 w-full bg-[color:var(--bg)] border border-[color:var(--border)] px-3 py-2 text-sm text-[color:var(--fg)] font-mono-num focus:border-sky-500/60 focus:outline-none"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={onMatchBroker}
                  disabled={matching || !matchTotal}
                  data-testid="match-broker-btn"
                  className="w-full inline-flex items-center justify-center gap-2 bg-sky-400 text-black px-4 py-2 text-sm hover:bg-sky-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {matching ? "MATCHING…" : "MATCH TO BROKER"}
                </button>
              </div>
            </div>
          </Card>


          {result && (
            <Card eyebrow="RESULT" title="Import summary" className={result.inserted > 0 ? "border-[rgba(34,197,94,0.30)]" : "border-rose-500/30"}>
              {/* Heads-up when trade count looks abnormally high — usually a sign
                  the CSV came in fill-by-fill and pairing didn't kick in. */}
              {(() => {
                const highCount = result.inserted > 20 && result.mode !== "fill_paired";
                if (!highCount) return null;
                return (
                  <div
                    className="mb-3 px-3 py-2 flex items-start gap-2 text-xs"
                    style={{ background: "rgba(245,158,11,0.10)", border: "1px solid rgba(245,158,11,0.35)", borderRadius: 8, color: "#F59E0B" }}
                    data-testid="import-high-count-warning"
                  >
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" strokeWidth={1.5} />
                    <div>
                      <div className="font-semibold" style={{ color: "#FBBF24" }}>Trade count looks high</div>
                      <div className="mt-0.5 text-[color:var(--fg-2)]">
                        {result.inserted} trades from {result.total_rows} rows. If your CSV was fill-by-fill (individual buys/sells), enable
                        <span className="font-mono-num"> ROLL FILLS INTO TRADES </span>
                        below and re-import to properly pair them into round-trip trades.
                      </div>
                    </div>
                  </div>
                );
              })()}
              <div className="flex items-center gap-3 mb-3">
                {result.inserted > 0
                  ? <CheckCircle2 className="h-5 w-5 text-[color:var(--accent)]" strokeWidth={1.5} />
                  : <AlertTriangle className="h-5 w-5 text-[color:var(--accent)]" strokeWidth={1.5} />}
                <div className="font-mono-num text-sm" data-testid="import-summary">
                  <span className="text-[color:var(--accent)]">{result.inserted}</span> inserted /
                  <span className="text-[color:var(--fg-2)]"> {result.total_rows}</span> rows /
                  <span className="text-rose-400"> {result.errors.length}</span> errors
                </div>
              </div>
              {result.detected_broker_name && (
                <div className="text-xs text-[color:var(--fg-3)] mb-3 flex flex-wrap items-center gap-3" data-testid="import-mode-badge">
                  <span>Detected format: <span className="text-[color:var(--accent)]">{result.detected_broker_name}</span></span>
                  {result.mode === "fill_paired" && (
                    <span className="px-2 py-0.5 bg-[rgba(34,197,94,0.10)] border border-[rgba(34,197,94,0.30)] text-[color:var(--accent)] font-mono-num text-[10px]">
                      FILL-PAIRED · {result.raw_fills} FILLS → {result.round_trips} ROUND-TRIPS
                    </span>
                  )}
                  {result.skipped_duplicates > 0 && (
                    <span className="px-2 py-0.5 bg-amber-500/10 border border-amber-500/30 text-amber-400 font-mono-num text-[10px]">
                      SKIPPED {result.skipped_duplicates} DUPLICATES
                    </span>
                  )}
                </div>
              )}
              {result.errors.length > 0 && (
                <div className="border border-[color:var(--border)] p-3 max-h-40 overflow-y-auto text-xs font-mono-num">
                  {result.errors.map((e, i) => (
                    <div key={i} className="text-rose-400">ROW {e.row}: {e.error}</div>
                  ))}
                </div>
              )}
              {result.inserted > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  <button onClick={() => navigate(`/recap/${new Date().toISOString().slice(0,10)}`)}
                    data-testid="goto-recap-btn"
                    className="bg-[color:var(--accent)] text-black px-4 py-2 text-sm hover:bg-[color:var(--accent)] transition-colors flex items-center gap-2">
                    DO TODAY&apos;S RECAP →
                  </button>
                  <button onClick={() => navigate("/")} data-testid="goto-dashboard-btn"
                    className="bg-[color:var(--surface)] hover:bg-[color:var(--surface2)] border border-[color:var(--border)] text-[color:var(--fg-2)] px-4 py-2 text-sm transition-colors">
                    GO TO DASHBOARD
                  </button>
                </div>
              )}
            </Card>
          )}
        </div>

        <Card eyebrow="SUPPORTED" title="Column requirements">
          <div className="text-xs text-[color:var(--fg-3)] mb-3">We auto-detect headers from IBKR, TD, Webull, Robinhood, MetaTrader, Tradovate & generic CSVs.</div>
          <div className="space-y-2">
            <div className="label-eyebrow">REQUIRED</div>
            {REQUIRED.map((c) => (
              <div key={c} className="flex items-center gap-2 text-xs font-mono-num">
                <span className="dot-marker text-[color:var(--accent)]" /> <span className="text-[color:var(--fg)]">{c}</span>
              </div>
            ))}
            <div className="label-eyebrow mt-4">OPTIONAL</div>
            {OPTIONAL.map((c) => (
              <div key={c} className="flex items-center gap-2 text-xs font-mono-num">
                <span className="dot-marker text-[color:var(--fg-3)]" /> <span className="text-[color:var(--fg-2)]">{c}</span>
              </div>
            ))}
          </div>

          <div className="mt-5 border border-[color:var(--border)] p-3 bg-[color:var(--surface)]">
            <div className="label-eyebrow mb-2 flex items-center gap-2"><FileText className="h-3 w-3" strokeWidth={1.5}/> SAMPLE</div>
            <pre className="text-[10px] font-mono-num text-[color:var(--fg-3)] overflow-x-auto whitespace-pre">{SAMPLE_CSV.slice(0, 220)}…</pre>
          </div>
        </Card>
      </div>
    </div>
  );
}
